import { OpenInNew } from "@mui/icons-material";
import {
  Link as MuiLink,
  type SxProps,
  type Theme,
  Typography,
  type TypographyProps,
} from "@mui/material";
import { Link, useMatchRoute } from "@tanstack/react-router";
import ReactMarkdown from "react-markdown";

import { mergeSx } from "../utils/mergeSx";

export interface MarkdownProps
  extends Omit<TypographyProps, "sx" | "children"> {
  children: string;
  /**
   * `sx` prop applied to the root paragraph component.
   */
  rootSx?: SxProps<Theme>;
  /**
   * `sx` styles applied to specific markdown elements.
   */
  componentSx?: {
    h1?: SxProps<Theme>;
    h2?: SxProps<Theme>;
    em?: SxProps<Theme>;
    a?: SxProps<Theme>;
    p?: SxProps<Theme>;
    li?: SxProps<Theme>;
    ol?: SxProps<Theme>;
    ul?: SxProps<Theme>;
  };
}

export function Markdown({
  children,
  rootSx,
  componentSx,
  ...props
}: MarkdownProps) {
  const matchRoute = useMatchRoute();

  return (
    <ReactMarkdown
      // NOTE: We skip HTML because we don't want to deal with the security implications of rendering arbitrary HTML.
      // We also unwrap disallowed elements to ensure that if someone tries to use an unsupported element,
      // it will still render the content instead of being completely stripped out.
      skipHtml
      unwrapDisallowed
      allowedElements={["p", "strong", "a", "h2", "em", "li", "ol", "ul", "h1"]}
      components={{
        li: ({ children }) => (
          <Typography
            component="li"
            variant="inherit"
            sx={mergeSx(
              (theme) => ({
                "&:not(:last-child)": { marginBottom: theme.spacing(1) },
              }),
              componentSx?.li,
            )}
          >
            {children}
          </Typography>
        ),
        ol: ({ children }) => (
          <Typography component="ol" variant="inherit" sx={componentSx?.ol}>
            {children}
          </Typography>
        ),
        ul: ({ children }) => (
          <Typography component="ul" variant="inherit" sx={componentSx?.ul}>
            {children}
          </Typography>
        ),
        em: ({ children }) => (
          <Typography
            component="span"
            variant="inherit"
            sx={{ fontStyle: "italic", ...componentSx?.em }}
          >
            {children}
          </Typography>
        ),
        h2: ({ children }) => (
          <Typography
            variant="body1"
            component="h2"
            color="inherit"
            sx={mergeSx(
              (theme) => ({
                fontWeight: theme.typography.fontWeightSemiBold,
              }),
              componentSx?.h2,
            )}
          >
            {children}
          </Typography>
        ),
        h1: ({ children }) => (
          <Typography
            variant="body1"
            component="h1"
            color="inherit"
            sx={mergeSx(
              (theme) => ({
                fontWeight: theme.typography.fontWeightSemiBold,
              }),
              componentSx?.h1,
            )}
          >
            {children}
          </Typography>
        ),
        p: ({ children }) => (
          <Typography variant="body2" sx={componentSx?.p} {...props}>
            {children}
          </Typography>
        ),
        strong: ({ children }) => (
          <Typography
            component="span"
            variant="inherit"
            sx={(theme) => ({
              fontWeight: theme.typography.fontWeightSemiBold,
              color: "inherit",
            })}
          >
            {children}
          </Typography>
        ),
        a: ({ href, children }) => {
          // NOTE: ReactMarkdown's default URL transform sanitizes unsafe protocols in `href`,
          // but it does not guarantee the value is parseable as a URL or that it should be treated
          // as external navigation. We still use `URL.canParse` here to decide between an external
          // link and an internal router link.
          // See: https://github.com/remarkjs/react-markdown#defaulturltransformurl
          if (href) {
            if (URL.canParse(href)) {
              return (
                <MuiLink
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={(theme) => ({
                    color: theme.palette.primary.main,
                    textDecoration: "none",
                  })}
                >
                  {children}
                  <OpenInNew
                    sx={{
                      fontSize: "inherit",
                      marginLeft: "2px",
                      marginBottom: "2px",
                      verticalAlign: "middle",
                    }}
                  />
                </MuiLink>
              );
            } else if (matchRoute({ to: href })) {
              return (
                <Link to={href} style={{ textDecoration: "none" }}>
                  <Typography
                    component="span"
                    variant="inherit"
                    sx={(theme) => ({
                      color: theme.palette.primary.main,
                    })}
                  >
                    {children}
                  </Typography>
                </Link>
              );
            }
          }

          return <>{children}</>;
        },
      }}
    >
      {children}
    </ReactMarkdown>
  );
}
